import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import {
  buildReservedSizeCss,
  type HostSlotReservedSize,
} from '../HostSlot/HostSlot';
import { useOptionalDataSourceManager } from '../../providers/DataSourceManager';
import {
  DECISIONS_CONTEXT_KEY,
  PLATFORM_VARIANT_CONTEXT_KEY,
  readDecisionAnswer,
  type DecisionAnswer,
} from '../../utils/decisions/embeddedDecisions';

/** The DSL node type the runtime renders as a variant. */
export { VARIANT_TYPE } from '../../utils/decisions/collectFactBindings';

export const VARIANT_SLOT_NAMES: readonly string[] = Array.from(
  { length: 8 },
  (_, index) => `outcomeSlot${index + 1}`,
);

const isSlotName = (value: unknown): value is string =>
  typeof value === 'string' && VARIANT_SLOT_NAMES.includes(value);

export interface VariantProps {
  readonly id?: string;
  readonly decisionId?: string;
  /** `{ [outcomeId]: 'outcomeSlotN' }`, as authored in Admin. */
  readonly outcomeSlots?: Readonly<Record<string, unknown>>;
  /** The slot rendered whenever the decision does not match an outcome. */
  readonly fallbackSlot?: string;
  /**
   * The space the block reserves at each breakpoint from the server render
   * onward, in the same shape as `platform.hostSlot`, so a post-load swap to
   * a slot no taller than it does not move the content below. `tablet` and
   * `desktop` inherit from the breakpoint below. Only `minHeight` is
   * rendered, so a taller slot still renders in full; `aspectRatio` is
   * ignored.
   */
  readonly reservedSize?: HostSlotReservedSize;
  /** Renders one slot's authored children. */
  readonly renderSlot: (slot: string) => React.ReactNode;
}

const VARIANT_RESERVED_SIZE_OPTIONS = { minHeightOnly: true } as const;

/**
 * The slot to show for a decision answer: the slot the map names for a
 * matched outcome, otherwise the fallback slot. An outcome the map does not
 * name also falls back, so a decision that gains an outcome never renders an
 * unauthored slot. Slots are chosen by outcome id, never by position.
 */
export function selectVariantSlot(
  props: Pick<VariantProps, 'outcomeSlots' | 'fallbackSlot'>,
  answer: { status: string; outcomeId?: string },
): string {
  const fallback = isSlotName(props.fallbackSlot)
    ? props.fallbackSlot
    : VARIANT_SLOT_NAMES[0];
  if (answer.status !== 'matched' || !answer.outcomeId) return fallback;
  const mapped = Object.prototype.hasOwnProperty.call(
    props.outcomeSlots ?? {},
    answer.outcomeId,
  )
    ? props.outcomeSlots?.[answer.outcomeId]
    : undefined;
  return isSlotName(mapped) ? mapped : fallback;
}

const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Limits layout shift when a post-load update swaps a block's slot. Before
 * the browser paints the new slot, the block keeps at least the height it
 * had, so content below it does not jump up while the new slot settles. The
 * held height never shrinks; nothing is reserved before the first swap.
 *
 * The height is measured on every render and, where the browser supports
 * it, whenever the mounted slot resizes between renders, so a slot that grew
 * after an image loaded or its text rewrapped reserves its real height.
 */
function useHeightHeldAcrossSwaps(slot: string) {
  const ref = useRef<HTMLDivElement>(null);
  const last = useRef<{ slot: string; height: number } | null>(null);
  const observer = useRef<
    { element: Element; observer: ResizeObserver } | undefined
  >(undefined);
  useIsomorphicLayoutEffect(() => {
    const element = ref.current;
    if (observer.current && observer.current.element !== element) {
      observer.current.observer.disconnect();
      observer.current = undefined;
    }
    if (!element) {
      last.current = null;
      return;
    }
    const previous = last.current;
    if (previous && previous.slot !== slot && previous.height > 0) {
      const held = Number.parseFloat(element.style.minHeight) || 0;
      element.style.minHeight = `${Math.max(previous.height, held)}px`;
    }
    last.current = { slot, height: element.getBoundingClientRect().height };
    if (!observer.current && typeof ResizeObserver !== 'undefined') {
      const resizes = new ResizeObserver(() => {
        if (last.current) {
          last.current = {
            slot: last.current.slot,
            height: element.getBoundingClientRect().height,
          };
        }
      });
      resizes.observe(element);
      observer.current = { element, observer: resizes };
    }
  });
  useEffect(
    () => () => {
      observer.current?.observer.disconnect();
      observer.current = undefined;
    },
    [],
  );
  return ref;
}

/**
 * Renders the slot its decision selects, from the outcome the page host
 * embedded in `context.decisions`. The answer is present at render, so there
 * is no loading state and no request. A page with no answer for the decision
 * renders the fallback slot. A post-load update to `context.decisions`
 * re-renders the block with the new answer, holding the block's height across
 * the swap.
 */
export const Variant: React.FC<VariantProps> = ({
  id,
  decisionId = '',
  outcomeSlots,
  fallbackSlot,
  reservedSize,
  renderSlot,
}) => {
  const className = `de-variant-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const reservedCss = useMemo(
    () =>
      buildReservedSizeCss(
        className,
        reservedSize,
        VARIANT_RESERVED_SIZE_OPTIONS,
      ),
    [className, reservedSize],
  );
  const flowContext = useOptionalDataSourceManager()?.flowContext;
  const variant = flowContext?.[PLATFORM_VARIANT_CONTEXT_KEY];
  const answer: DecisionAnswer = readDecisionAnswer(
    flowContext?.[DECISIONS_CONTEXT_KEY],
    decisionId,
    typeof variant === 'string' ? variant : undefined,
  );
  const slot = selectVariantSlot(
    { outcomeSlots, fallbackSlot },
    {
      status: answer.status,
      outcomeId: answer.status === 'matched' ? answer.outcomeId : undefined,
    },
  );
  const ref = useHeightHeldAcrossSwaps(slot);
  return (
    <div
      ref={ref}
      className={reservedCss ? className : undefined}
      data-variant-id={id}
      data-variant-state={answer.status === 'matched' ? 'matched' : 'fallback'}
      data-variant-status={answer.status}
      data-variant-slot={slot}
    >
      {reservedCss ? <style>{reservedCss}</style> : null}
      {renderSlot(slot)}
    </div>
  );
};
